/**
 * The 10 GMS languages, covering all 11 SEA Games countries (Malaysia and
 * Brunei share Malay; Singapore's working language is English).
 *
 * Codes are standard ISO 639 with ONE deliberate exception: this platform
 * writes Malay as "bm", not ISO "ms". "bm" is a platform-wide convention and
 * appears in storage, <html lang> and the picker — but it collides with ISO
 * 639-1 Bambara, so it must NEVER reach an Intl API. Every Intl call goes
 * through intlLocaleFor() below, which is the single place the quirk lives.
 * (i18next itself is fine with "bm": Bambara and Malay happen to share the
 * same CLDR plural category set, {other}.)
 */

export const SUPPORTED_LOCALES = [
	"en",
	"bm",
	"id",
	"th",
	"vi",
	"fil",
	"my",
	"km",
	"lo",
	"tet",
] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

export function isSupportedLocale(value: string): value is Locale {
	return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Endonyms — every language names itself, so the picker is readable to the
 * person who needs it precisely when the UI is in the wrong language. */
export const LOCALE_NAMES: Record<Locale, string> = {
	en: "English",
	bm: "Bahasa Melayu",
	id: "Bahasa Indonesia",
	th: "ไทย",
	vi: "Tiếng Việt",
	fil: "Filipino",
	my: "မြန်မာ",
	km: "ខ្មែរ",
	lo: "ລາວ",
	tet: "Tetun",
};

/**
 * The locale actually handed to Intl.DateTimeFormat / Intl.NumberFormat.
 *
 * - "bm" would resolve as Bambara → map to real ISO "ms".
 * - "tet" has no CLDR data in any browser → Portuguese-derived month names are
 *   the Timorese convention; a Timorese reviewer should confirm ("pt" pending).
 * - Everything else ("fil", "my", "km", "lo", …) has CLDR data and passes
 *   through unchanged.
 */
export function intlLocaleFor(locale: string): string {
	if (locale === "bm") return "ms";
	if (locale === "tet") return "pt";
	return locale;
}
